"use client";

import { useState, type FC } from "react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import * as RadixPopover from "@radix-ui/react-popover";
import { cn } from "@/lib/cn";
import { useRipple } from "@/hooks/use-ripple";

type DefinitionTagProps = {
  label: string;
  definition?: string;
  className?: string;
};

export const DefinitionTag: FC<DefinitionTagProps> = ({ label, definition, className }) => {
  const [open, setOpen] = useState(false);
  const closeRipple = useRipple();

  if (!definition) {
    return <span className={className}>{label}</span>;
  }

  return (
    <RadixPopover.Root open={open} onOpenChange={setOpen}>
      <RadixPopover.Trigger asChild>
        <span
          role="button"
          tabIndex={0}
          onMouseEnter={() => setOpen(true)}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            setOpen((prev) => !prev);
          }}
          onKeyDown={(event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            setOpen((prev) => !prev);
          }}
          className={cn(
            "cursor-pointer rounded-small underline decoration-dotted underline-offset-2 outline-none focus-visible:ring-2 focus-visible:ring-brand-blue",
            className,
          )}
        >
          {label}
        </span>
      </RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          side="top"
          sideOffset={8}
          collisionPadding={16}
          onClick={(event) => event.stopPropagation()}
          className="z-50 flex w-324 flex-col gap-16 rounded-medium border border-neutral-light-grey bg-neutral-white p-24 text-left data-[state=closed]:animate-[fade-out_150ms_ease-in] data-[state=open]:animate-[fade-in_150ms_ease-out]"
        >
          <div className="flex items-start justify-between gap-24">
            <p className="min-w-0 flex-1 text-16 leading-[1.4] font-medium text-brand-dark-blue">{label}</p>
            <button
              type="button"
              aria-label="Close"
              onPointerDown={closeRipple.addRipple}
              onClick={() => setOpen(false)}
              className="relative size-24 shrink-0 cursor-pointer overflow-hidden rounded-full text-neutral-black outline-none hover:bg-neutral-black/5 active:bg-neutral-black/10 focus-visible:ring-2 focus-visible:ring-brand-blue"
            >
              <XMarkIcon aria-hidden />
              {closeRipple.ripples.map((ripple) => (
                <span
                  key={ripple.id}
                  onAnimationEnd={() => closeRipple.removeRipple(ripple.id)}
                  style={{ left: ripple.x, top: ripple.y, width: ripple.size, height: ripple.size }}
                  className="pointer-events-none absolute rounded-full bg-neutral-black/15 animate-[ripple_600ms_ease-out]"
                />
              ))}
            </button>
          </div>
          <p className="text-14 leading-[1.4] text-neutral-black/50">{definition}</p>
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
};
